import type { AssetType, CreativeMediaItem, DeleteMediaResult } from '@marketing-tool/shared';
import pLimit from 'p-limit';

import { validateCreativeFile } from '../lib/creative-limits';
import type { FileValidationResult } from '../lib/creative-limits';
import { trpc } from '../lib/trpc';
import { useTrpcQuery } from '../lib/trpc-hooks';

export type { AssetType, CreativeMediaItem, DeleteMediaResult };
export { validateCreativeFile, type FileValidationResult };

export function requestFiles(): Promise<FileList | undefined> {
    return new Promise((resolve) => {
        const input = document.createElement('input');
        input.type = 'file';
        input.multiple = true;
        input.accept = 'image/*';

        input.onchange = () => {
            resolve(input.files ?? undefined);
        };
        input.oncancel = () => {
            resolve(undefined);
        };
        input.onerror = () => {
            resolve(undefined);
        };
        input.click();
    });
}

export function requestCreativeFiles(): Promise<FileList | undefined> {
    return new Promise((resolve) => {
        const input = document.createElement('input');
        input.type = 'file';
        input.multiple = true;
        input.accept = 'image/*,video/*';

        input.onchange = () => {
            resolve(input.files ?? undefined);
        };
        input.oncancel = () => {
            resolve(undefined);
        };
        input.onerror = () => {
            resolve(undefined);
        };
        input.click();
    });
}

interface MediaDimensions {
    width: number;
    height: number;
}

export async function getFileDimensions(file: File): Promise<MediaDimensions> {
    return new Promise((resolve, reject) => {
        if (file.type.startsWith('image/')) {
            const img = new Image();
            const objectUrl = URL.createObjectURL(file);

            img.onload = () => {
                URL.revokeObjectURL(objectUrl);
                resolve({
                    width: img.naturalWidth,
                    height: img.naturalHeight,
                });
            };

            img.onerror = () => {
                URL.revokeObjectURL(objectUrl);
                reject(new Error(`Failed to load image: ${file.name}`));
            };

            img.src = objectUrl;
        } else if (file.type.startsWith('video/')) {
            const video = document.createElement('video');
            const objectUrl = URL.createObjectURL(file);

            video.onloadedmetadata = () => {
                URL.revokeObjectURL(objectUrl);
                resolve({
                    width: video.videoWidth,
                    height: video.videoHeight,
                });
            };

            video.onerror = () => {
                URL.revokeObjectURL(objectUrl);
                reject(new Error(`Failed to load video: ${file.name}`));
            };

            video.src = objectUrl;
            video.preload = 'metadata';
        } else {
            reject(new Error(`Unsupported file type: ${file.type}`));
        }
    });
}

interface UploadMediaOptions {
    gameId: string;
    files: FileList;
}

interface UploadMediaResult {
    csv: string;
}

export async function uploadMedia(opts: UploadMediaOptions): Promise<UploadMediaResult> {
    // Step 1: Request presigned URLs via tRPC
    const prepare = await trpc.line.media.prepareUpload.mutate({ count: opts.files.length });

    // Step 2: Upload each file directly to S3 (not through the backend)
    const uploads = await Promise.all(
        prepare.items.map(async (item, idx) => {
            const formData = new FormData();
            for (const [param, value] of Object.entries(item.fields)) {
                formData.append(param, value);
            }

            // S3 requires file to be last in the form data
            const file = opts.files[idx]!;
            formData.append('file', file);

            await fetch(`https://s3.amazonaws.com/${prepare.bucket}`, {
                method: 'POST',
                body: formData,
                mode: 'no-cors',
            });

            return {
                name: file.name,
                mimeType: file.type,
                key: item.key,
            };
        }),
    );

    // Step 3: Register uploaded media via tRPC (backend downloads from S3 and uploads to LINE)
    const medias = await trpc.line.media.upload.mutate({
        gameId: opts.gameId,
        uploads,
    });

    return {
        csv: medias.map((media) => [media.id, media.name, media.mediaType].join(', ')).join('\n'),
    };
}

export interface DeleteMediaOptions {
    gameId: string;
    mediaIds: number[];
}

export async function deleteMedia(opts: DeleteMediaOptions): Promise<DeleteMediaResult> {
    return trpc.line.media.delete.mutate({
        gameId: opts.gameId,
        mediaIds: opts.mediaIds,
    });
}

export function getAssetType(mediaItem: CreativeMediaItem): AssetType {
    const mimeType = mediaItem.mimeType.toLowerCase();

    // Check for APNG (animated PNG)
    if (mimeType === 'image/apng' || (mimeType === 'image/png' && mediaItem.name.toLowerCase().endsWith('.apng'))) {
        return 'animated-image';
    }

    // Check for video
    if (mimeType.startsWith('video/')) {
        return 'video';
    }

    // Default to image
    return 'image';
}

export function getAssetTypeLabel(assetType: AssetType): string {
    const labels: Record<AssetType, string> = {
        image: 'Image',
        video: 'Video',
        'animated-image': 'Animated Image',
    };
    return labels[assetType];
}

export async function fetchCreativeMedia(gameId: string): Promise<CreativeMediaItem[]> {
    return trpc.games.creativeMedia.list.query({ gameId });
}

export const useCreativeMedia = (gameId: string) =>
    useTrpcQuery((signal) => trpc.games.creativeMedia.list.query({ gameId }, { signal }), [gameId]);

// Creative media upload workflow
interface CreativeUploadOptions {
    gameId: string;
    creatives: {
        file: File;
        marketingFields: {
            week: string;
            hasTextOverlay: boolean;
            hasCallToAction: boolean;
            additionalText?: string;
        };
    }[];
}

interface CreativeUploadResult {
    uploaded: number;
    registered: number;
}

export async function uploadCreativeMedia(opts: CreativeUploadOptions): Promise<CreativeUploadResult> {
    const { gameId, creatives } = opts;

    // Create a limit function that allows max 10 concurrent operations
    const limit = pLimit(10);

    // Step 1: Generate UUIDs for each creative, detect dimensions, and request presigned URLs
    const uploads = await Promise.all(
        creatives.map((creative) =>
            limit(async () => {
                const dimensions = await getFileDimensions(creative.file);
                return {
                    mediaId: crypto.randomUUID(),
                    file: creative.file,
                    marketingFields: creative.marketingFields,
                    width: dimensions.width,
                    height: dimensions.height,
                };
            }),
        ),
    );

    const urlResponse = await trpc.games.creativeMedia.getUploadUrls.mutate({
        gameId,
        uploads: uploads.map(({ mediaId, file }) => ({
            mediaId,
            contentType: file.type,
        })),
    });

    // Step 2: Upload files to S3 using the presigned URLs
    const uploadPromises = uploads.map(({ mediaId, file, marketingFields, width, height }) =>
        limit(async () => {
            const urlData = urlResponse.find((item) => item.mediaId === mediaId);
            if (!urlData) {
                throw new Error(`Upload URL not found for media ID: ${mediaId}`);
            }

            const response = await fetch(urlData.uploadUrl, {
                method: 'PUT',
                body: file,
                headers: {
                    'Content-Type': file.type,
                },
            });

            if (!response.ok) {
                throw new Error(`Failed to upload file ${file.name}: ${response.statusText}`);
            }
            return {
                mediaId,
                mimeType: file.type,
                width,
                height,
                marketingFields,
            };
        }),
    );

    const uploadResults = await Promise.all(uploadPromises);

    // Step 3: Register the uploaded media in the database
    const registerResponse = await trpc.games.creativeMedia.register.mutate({
        gameId,
        uploads: uploadResults,
    });

    return {
        uploaded: uploadResults.length,
        registered: registerResponse.registered,
    };
}

export interface DeleteCreativeMediaOptions {
    gameId: string;
    mediaIds: string[];
}

export async function deleteCreativeMedia(opts: DeleteCreativeMediaOptions): Promise<void> {
    await trpc.games.creativeMedia.delete.mutate({
        gameId: opts.gameId,
        mediaIds: opts.mediaIds,
    });
}

export interface UpdateCreativeMediaOptions {
    gameId: string;
    updates: Record<
        string,
        {
            week?: string;
            hasTextOverlay?: boolean;
            hasCallToAction?: boolean;
            additionalText?: string;
        }
    >;
}

export async function updateCreativeMedia(opts: UpdateCreativeMediaOptions): Promise<void> {
    await trpc.games.creativeMedia.update.mutate({
        gameId: opts.gameId,
        updates: opts.updates,
    });
}
